"use client";

import { useState } from "react";
import { storyblokEditable } from "@storyblok/react";
import Image from "next/image";
import { sbBlok } from "@/types/sb-types";
import clsx from "clsx";

interface CourseLibraryProps {
  blok: sbBlok;
}

const CourseLibrary = ({ blok }: CourseLibraryProps) => {
  const [selectedRound, setSelectedRound] = useState(blok.content[0]?.round_id);
  return (
    <div
      className={clsx("grid-container", {
        "pt-7 lg:pt-14":
          !!blok.spacing_top?.length && blok.spacing_top[0].option === "large",
        "pt-14 lg:pt-[120px]":
          !!blok.spacing_top?.length &&
          blok.spacing_top[0].option === "x_large",
        "pt-[120px] lg:pt-[180px]":
          !!blok.spacing_top?.length &&
          blok.spacing_top[0].option === "xx_large",
        "pb-7 lg:pb-14":
          !!blok.spacing_bottom?.length &&
          blok.spacing_bottom[0].option === "large",
        "pb-14 lg:pb-[120px]":
          !!blok.spacing_bottom?.length &&
          blok.spacing_bottom[0].option === "x_large",
        "pb-[120px] lg:pb-[180px]":
          !!blok.spacing_bottom?.length &&
          blok.spacing_bottom[0].option === "xx_large",
      })}
    >
      <div className="col-start-1 col-span-4 row-start-1 lg:col-start-2 lg:col-span-14 2xl:col-start-2 2xl:col-span-30">
        <div {...storyblokEditable(blok)}>
          <ul className="flex gap-2.5 justify-evenly list-none my-[60px] p-0 flex-wrap lg:flex-nowrap">
            {blok.content.map((c: sbBlok) => (
              <li
                key={c._uid}
                onClick={() => setSelectedRound(c.round_id)}
                className="bg-tm-c-gray-950 py-[15px] rounded-sm flex-grow basis-[31%] cursor-pointer text-center transition-colors duration-300 lg:py-5 lg:hover:bg-tm-c-gray-700"
                style={{
                  background:
                    c.round_id === selectedRound
                      ? "var(--color-tm-c-gray-700)"
                      : "",
                }}
              >
                {c.round_name}
              </li>
            ))}
          </ul>
          <div className="grid grid-cols-1 w-full">
            {blok.content.map((c: sbBlok, i: number) => (
              <div
                key={c._uid}
                className="flex flex-col gap-5 opacity-0 row-start-1 col-start-1 transition-opacity duration-300 pointer-events-none lg:flex-row"
                style={{
                  opacity: c.round_id === selectedRound ? "1" : "",
                }}
              >
                <div className="w-full h-[200px] relative rounded-[10px] overflow-hidden min-[461px]:h-[300px] min-[661px]:h-[400px] lg:w-2/5 lg:h-auto">
                  <Image
                    src={c.image.filename}
                    alt={c.image.alt}
                    fill
                    className="object-cover"
                    sizes="(max-width: 960px) 100vw, 40vw"
                    priority={i === 0}
                  />
                </div>
                <div className="w-full p-5 bg-tm-c-gray-950 rounded-[10px] lg:w-3/5 lg:p-[60px]">
                  <h3 className="mb-2.5 mt-0 mx-0 text-tm-c-brand font-normal text-xl">
                    {c.round_name}
                  </h3>
                  <h2 className="m-0 font-normal text-[30px] leading-[1.2]">
                    {c.round_title}
                  </h2>
                  <p className="mt-0 mx-0 mb-10">{c.round_date}</p>
                  <p className="text-[15px] m-0">{c.round_info}</p>
                  <div className="flex gap-5 mt-[60px]">
                    <ul className="list-none m-0 p-0 w-full 2xl:w-[70%]">
                      <li className="flex gap-5 mt-1.5 text-[15px] opacity-60 first:opacity-100 first:mt-0">
                        <span className="block w-full">
                          {c.round_info_list[0]?.title}
                        </span>
                        <span className="block w-full">
                          {c.round_info_list[1]?.title}
                        </span>
                      </li>
                      {c.round_info_list[0]?.content.map(
                        (ci: sbBlok, i: number) => (
                          <li
                            key={ci._uid}
                            className="flex gap-5 mt-1.5 text-[15px] opacity-60 first:opacity-100 first:mt-0"
                          >
                            <span className="block w-full">{ci.content}</span>
                            <span className="block w-full">
                              {c.round_info_list[1]?.content[i].content}
                            </span>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseLibrary;
